/**
 * Mahalaxmi location picker
 * Isolated module: Leaflet is loaded only when the map step opens.
 */
(function () {
  const LEAFLET_CSS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
  const LEAFLET_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
  const PHOTON = 'https://photon.komoot.io';

  const boot = () => {
    const configEl = document.getElementById('mlp-config');
    const root = document.querySelector('[data-mlp-root]');
    if (!configEl || !root) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }
    if (!config.enabled) return;

    const picker = new LocationPicker(root, config);
    picker.init();
    window.MahalaxmiLocation = picker;
  };

  class LocationPicker {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.strings = config.strings || {};
      this.dialog = root.querySelector('[data-mlp-dialog]');
      this.searchInput = root.querySelector('[data-mlp-search]');
      this.suggestEl = root.querySelector('[data-mlp-suggest]');
      this.statusEl = root.querySelector('[data-mlp-status]');
      this.artEl = root.querySelector('[data-mlp-art]');
      this.mapWrap = root.querySelector('[data-mlp-map-wrap]');
      this.mapEl = root.querySelector('[data-mlp-map]');
      this.mapHint = root.querySelector('[data-mlp-map-hint]');
      this.enableBtn = root.querySelector('[data-mlp-enable]');
      this.confirmBtn = root.querySelector('[data-mlp-confirm]');
      this.gate = document.querySelector('[data-mlp-gate]');
      this.pending = null;
      this.map = null;
      this.marker = null;
      this.searchTimer = null;
      this.leafletPromise = null;
      this.googlePromise = null;
      this.gmap = null;
      this.gmarker = null;
      this.placesService = null;
    }

    usesGoogle() {
      return this.config.provider === 'google' && Boolean(this.config.googleApiKey);
    }

    init() {
      if (this.root.parentElement !== document.body) {
        document.body.appendChild(this.root);
      }
      this.bindEvents();

      if (window.Shopify && window.Shopify.designMode) {
        document.documentElement.classList.add('mlp-geo-served');
        document.documentElement.classList.remove('mlp-page-blocked');
        this.syncTrigger(this.readLocation());
        return;
      }

      this.applyLocation(this.readLocation(), { persist: false });
      this.maybeDetectFirstVisit();
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-mlp-open]');
        if (opener) {
          event.preventDefault();
          this.open();
          return;
        }
        if (event.target.closest('[data-mlp-close]')) {
          this.close();
        }
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      this.searchInput.addEventListener('input', () => {
        const query = this.searchInput.value.trim();
        clearTimeout(this.searchTimer);
        if (query.length < 3) {
          this.renderSuggestions([]);
          return;
        }
        this.searchTimer = setTimeout(() => this.searchAddress(query), 280);
      });

      this.enableBtn.addEventListener('click', () => this.useCurrentLocation(false));
      this.confirmBtn.addEventListener('click', () => this.confirmPending());
    }

    storageKey() {
      return this.config.storageKey || 'mahalaxmi_delivery_location';
    }

    readLocation() {
      try {
        const loc = JSON.parse(localStorage.getItem(this.storageKey()) || 'null');
        if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null;
        return loc;
      } catch (error) {
        return null;
      }
    }

    writeLocation(loc) {
      localStorage.setItem(this.storageKey(), JSON.stringify(loc));
    }

    distanceKm(lat, lng) {
      const toRad = Math.PI / 180;
      const dLat = (lat - this.config.storeLat) * toRad;
      const dLng = (lng - this.config.storeLng) * toRad;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.config.storeLat * toRad) *
          Math.cos(lat * toRad) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
    }

    isServed(loc) {
      if (!loc) return false;
      return this.distanceKm(loc.lat, loc.lng) <= Number(this.config.radiusKm || 10);
    }

    applyLocation(loc, options = {}) {
      const persist = options.persist !== false;
      if (persist) {
        if (loc) this.writeLocation(loc);
        else localStorage.removeItem(this.storageKey());
      }

      const served = this.isServed(loc);
      document.documentElement.classList.toggle('mlp-geo-served', served);
      document.documentElement.classList.toggle(
        'mlp-page-blocked',
        Boolean(this.config.isRestrictedPage && !served)
      );

      this.syncTrigger(loc);
      this.syncGate(loc, served);
      this.moveGate();
    }

    syncTrigger(loc) {
      const fullLabel = loc && loc.label ? loc.label : '';
      const short = this.shortLabel(fullLabel || this.config.placeholder || 'Select Location');

      document.querySelectorAll('[data-mlp-trigger-label]').forEach((el) => {
        el.textContent = short;
        el.title = fullLabel;
      });

      document.querySelectorAll('[data-mlp-trigger-title]').forEach((el) => {
        el.textContent = short;
      });

      document.querySelectorAll('[data-mlp-trigger-subline]').forEach((el) => {
        const subline = fullLabel
          ? this.truncateSubline(fullLabel)
          : this.config.eyebrow || this.config.placeholder || 'Select delivery location';
        el.textContent = subline;
        el.title = fullLabel || subline;
      });
    }

    truncateSubline(label, max = 42) {
      const clean = String(label || '').replace(/\s+/g, ' ').trim();
      if (!clean) return this.config.eyebrow || this.config.placeholder || 'Select delivery location';
      return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
    }

    shortLabel(label) {
      const clean = String(label || '').replace(/\s+/g, ' ').trim();
      if (!clean) return this.config.placeholder || 'Select Location';
      const first = clean.split(',')[0];
      return first.length > 22 ? `${first.slice(0, 20)}…` : first;
    }

    syncGate(loc, served) {
      if (!this.gate) return;
      const titleEl = this.gate.querySelector('[data-mlp-gate-title]');
      const textEl = this.gate.querySelector('[data-mlp-gate-text]');
      if (!this.config.isRestrictedPage || served) {
        this.gate.hidden = true;
        return;
      }
      this.gate.hidden = false;
      if (titleEl) titleEl.textContent = this.strings.blockTitle || '';
      if (textEl) {
        textEl.textContent = loc ? this.strings.outsideMessage : this.strings.blockMessage;
      }
    }

    moveGate() {
      const main = document.getElementById('MainContent');
      if (main && this.gate && this.gate.parentElement !== main) {
        main.prepend(this.gate);
      }
    }

    maybeDetectFirstVisit() {
      if (this.readLocation()) return;
      if (sessionStorage.getItem('mlp_gps_attempted')) return;
      sessionStorage.setItem('mlp_gps_attempted', '1');
      this.useCurrentLocation(true);
    }

    useCurrentLocation(silent) {
      if (!navigator.geolocation) {
        if (!silent) this.setStatus(this.strings.geoError);
        this.applyLocation(null);
        return;
      }

      const btnLabel = this.enableBtn.querySelector('.mlp-current__btn');
      if (!silent && btnLabel) btnLabel.textContent = this.strings.enabling || 'Detecting…';

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (btnLabel) btnLabel.textContent = this.strings.enable || 'Enable';
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const label = await this.reverseGeocode(lat, lng);
          if (silent) {
            this.applyLocation({ lat, lng, label, source: 'gps' });
            return;
          }
          this.showMapStep({ lat, lng, label, source: 'gps' });
        },
        () => {
          if (btnLabel) btnLabel.textContent = this.strings.enable || 'Enable';
          if (!silent) this.setStatus(this.strings.geoDenied);
          this.applyLocation(null, { persist: !silent });
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    }

    async searchAddress(query) {
      if (this.usesGoogle()) {
        try {
          const items = await this.searchGoogle(query);
          this.renderSuggestions(items);
          if (!items.length) this.setStatus(this.strings.searchError);
          else this.setStatus('');
        } catch (error) {
          this.renderSuggestions([]);
          this.setStatus(this.strings.searchError);
        }
        return;
      }

      try {
        const url =
          `${PHOTON}/api/?q=${encodeURIComponent(query)}&limit=6&lang=en` +
          `&lat=${encodeURIComponent(this.config.storeLat)}&lon=${encodeURIComponent(this.config.storeLng)}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('search failed');
        const data = await response.json();
        let items = (data.features || []).map((feature) => this.featureToPlace(feature)).filter(Boolean);
        if (!items.length) items = await this.searchNominatim(query);
        this.renderSuggestions(items);
        if (!items.length) this.setStatus(this.strings.searchError);
        else this.setStatus('');
      } catch (error) {
        try {
          const items = await this.searchNominatim(query);
          this.renderSuggestions(items);
          if (!items.length) this.setStatus(this.strings.searchError);
          else this.setStatus('');
        } catch (fallbackError) {
          this.renderSuggestions([]);
          this.setStatus(this.strings.searchError);
        }
      }
    }

    async searchNominatim(query) {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=${encodeURIComponent(query)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const data = await response.json();
      return (data || []).map((row) => ({
        lat: Number(row.lat),
        lng: Number(row.lon),
        label: row.display_name,
        source: 'search',
      }));
    }

    featureToPlace(feature) {
      const coords = feature && feature.geometry && feature.geometry.coordinates;
      const props = (feature && feature.properties) || {};
      if (!coords || coords.length < 2) return null;
      return {
        lat: Number(coords[1]),
        lng: Number(coords[0]),
        label: this.labelFromProps(props),
        source: 'search',
      };
    }

    labelFromProps(props) {
      const parts = [props.name, props.street, props.locality || props.city, props.county, props.state, props.postcode]
        .map((part) => (part ? String(part).trim() : ''))
        .filter((part, index, arr) => part && arr.indexOf(part) === index);
      return parts.join(', ') || 'Selected location';
    }

    async reverseGeocode(lat, lng) {
      if (this.usesGoogle()) return this.reverseGeocodeGoogle(lat, lng);
      try {
        const response = await fetch(`${PHOTON}/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`);
        if (!response.ok) throw new Error('reverse failed');
        const data = await response.json();
        const feature = data.features && data.features[0];
        if (!feature) return this.fallbackLabel(lat, lng);
        return this.labelFromProps(feature.properties || {});
      } catch (error) {
        return this.fallbackLabel(lat, lng);
      }
    }

    fallbackLabel(lat, lng) {
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }

    renderSuggestions(items) {
      if (!items.length) {
        this.suggestEl.hidden = true;
        this.suggestEl.innerHTML = '';
        return;
      }

      this.suggestEl.hidden = false;
      this.suggestEl.innerHTML = items
        .map((item) => {
          const name = this.escape((item.label || '').split(',')[0]);
          const meta = this.escape(item.label || '');
          const placeId = item.placeId ? ` data-place-id="${this.escapeAttr(item.placeId)}"` : '';
          const lat = item.lat != null ? ` data-lat="${item.lat}"` : '';
          const lng = item.lng != null ? ` data-lng="${item.lng}"` : '';
          return `<li><button type="button" class="mlp-suggest__item"${placeId}${lat}${lng} data-label="${this.escapeAttr(item.label || '')}"><span class="mlp-suggest__name">${name}</span><span class="mlp-suggest__meta">${meta}</span></button></li>`;
        })
        .join('');

      this.suggestEl.querySelectorAll('.mlp-suggest__item').forEach((button) => {
        button.addEventListener('click', async () => {
          let loc = {
            lat: button.dataset.lat ? Number(button.dataset.lat) : null,
            lng: button.dataset.lng ? Number(button.dataset.lng) : null,
            label: button.dataset.label,
            source: 'search',
          };
          if (button.dataset.placeId) {
            loc = await this.googlePlaceDetails(button.dataset.placeId);
          }
          if (loc && loc.lat != null) this.showMapStep(loc);
        });
      });
    }

    escape(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    escapeAttr(value) {
      return this.escape(value).replace(/"/g, '&quot;');
    }

    setStatus(message) {
      if (!this.statusEl) return;
      if (!message) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = message;
    }

    open() {
      const drawer = document.querySelector('header-drawer details.menu-drawer-container');
      if (drawer && drawer.hasAttribute('open')) drawer.removeAttribute('open');

      this.root.hidden = false;
      document.body.classList.add('mlp-modal-open');
      this.setStatus('');
      const loc = this.readLocation() || this.pending;
      if (loc) {
        this.showMapStep(loc);
      } else {
        this.showSearchStep();
        setTimeout(() => this.searchInput && this.searchInput.focus(), 50);
      }
    }

    close() {
      this.root.hidden = true;
      document.body.classList.remove('mlp-modal-open');
    }

    showSearchStep() {
      if (this.artEl) this.artEl.hidden = false;
      if (this.mapWrap) this.mapWrap.hidden = true;
      if (this.enableBtn) this.enableBtn.hidden = false;
    }

    async showMapStep(loc) {
      this.pending = loc;
      this.renderSuggestions([]);
      if (this.searchInput) this.searchInput.value = loc.label || '';
      if (this.artEl) this.artEl.hidden = true;
      if (this.enableBtn) this.enableBtn.hidden = true;
      if (this.mapWrap) this.mapWrap.hidden = false;
      if (this.mapHint) this.mapHint.textContent = this.strings.confirmHint || '';
      this.setStatus('');

      if (this.usesGoogle()) {
        await this.ensureGoogle();
        this.drawGoogleMap(loc);
        return;
      }

      await this.ensureLeaflet();
      this.drawLeafletMap(loc);
    }

    loadResource(href, type) {
      return new Promise((resolve, reject) => {
        const isCss = type === 'css';
        const selector = isCss ? `link[href="${href}"]` : `script[src="${href}"]`;
        if (document.querySelector(selector)) {
          resolve();
          return;
        }
        const el = document.createElement(isCss ? 'link' : 'script');
        if (isCss) {
          el.rel = 'stylesheet';
          el.href = href;
        } else {
          el.src = href;
        }
        el.onload = () => resolve();
        el.onerror = () => reject(new Error('asset failed'));
        document.head.appendChild(el);
      });
    }

    ensureLeaflet() {
      if (window.L) return Promise.resolve();
      if (this.leafletPromise) return this.leafletPromise;
      this.leafletPromise = this.loadResource(LEAFLET_CSS, 'css').then(() => this.loadResource(LEAFLET_JS, 'js'));
      return this.leafletPromise;
    }

    ensureGoogle() {
      if (window.google && window.google.maps && window.google.maps.places) {
        return Promise.resolve();
      }
      if (this.googlePromise) return this.googlePromise;

      const key = this.config.googleApiKey;
      this.googlePromise = new Promise((resolve, reject) => {
        window.mlpGoogleMapsReady = () => resolve();
        const el = document.createElement('script');
        el.src =
          `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
          `&libraries=places&v=weekly&callback=mlpGoogleMapsReady`;
        el.async = true;
        el.defer = true;
        el.onerror = () => reject(new Error('Google Maps failed to load'));
        document.head.appendChild(el);
      });
      return this.googlePromise;
    }

    async searchGoogle(query) {
      await this.ensureGoogle();
      return new Promise((resolve) => {
        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          {
            input: query,
            componentRestrictions: { country: 'in' },
            location: new google.maps.LatLng(Number(this.config.storeLat), Number(this.config.storeLng)),
            radius: 25000,
          },
          (predictions, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
              resolve([]);
              return;
            }
            resolve(
              predictions.slice(0, 6).map((prediction) => ({
                placeId: prediction.place_id,
                label: prediction.description,
                source: 'search',
              }))
            );
          }
        );
      });
    }

    async googlePlaceDetails(placeId) {
      await this.ensureGoogle();
      if (!this.placesService) {
        this.placesDummy = document.createElement('div');
        this.placesService = new google.maps.places.PlacesService(this.placesDummy);
      }
      return new Promise((resolve) => {
        this.placesService.getDetails(
          {
            placeId,
            fields: ['geometry', 'formatted_address', 'name'],
          },
          (place, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !place || !place.geometry) {
              resolve(null);
              return;
            }
            resolve({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              label: place.formatted_address || place.name,
              source: 'search',
            });
          }
        );
      });
    }

    async reverseGeocodeGoogle(lat, lng) {
      await this.ensureGoogle();
      return new Promise((resolve) => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0].formatted_address);
            return;
          }
          resolve(this.fallbackLabel(lat, lng));
        });
      });
    }

    drawLeafletMap(loc) {
      if (!window.L || !this.mapEl) return;
      if (window.L.Icon && window.L.Icon.Default) {
        window.L.Icon.Default.imagePath = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/';
      }

      if (!this.map) {
        this.map = window.L.map(this.mapEl, { scrollWheelZoom: false });
        window.L
          .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
            maxZoom: 19,
          })
          .addTo(this.map);
        this.map.on('click', (event) => {
          this.setLeafletMarker(event.latlng.lat, event.latlng.lng, true);
        });
      }

      this.map.setView([loc.lat, loc.lng], 15);
      this.setLeafletMarker(loc.lat, loc.lng, false);
      setTimeout(() => this.map.invalidateSize(), 80);
    }

    drawGoogleMap(loc) {
      if (!window.google || !this.mapEl) return;
      const center = { lat: loc.lat, lng: loc.lng };

      if (!this.gmap) {
        this.gmap = new google.maps.Map(this.mapEl, {
          center,
          zoom: 16,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        });
        this.gmap.addListener('click', (event) => {
          this.setGoogleMarker(event.latLng.lat(), event.latLng.lng(), true);
        });
      } else {
        this.gmap.setCenter(center);
      }

      this.setGoogleMarker(loc.lat, loc.lng, false);
      setTimeout(() => {
        google.maps.event.trigger(this.gmap, 'resize');
        this.gmap.setCenter(center);
      }, 80);
    }

    async setLeafletMarker(lat, lng, reverse) {
      if (!this.map) return;
      if (this.marker) this.marker.setLatLng([lat, lng]);
      else {
        this.marker = window.L.marker([lat, lng], { draggable: true }).addTo(this.map);
        this.marker.on('dragend', () => {
          const pos = this.marker.getLatLng();
          this.setLeafletMarker(pos.lat, pos.lng, true);
        });
      }
      await this.updatePending(lat, lng, reverse);
    }

    async setGoogleMarker(lat, lng, reverse) {
      if (!this.gmap) return;
      const position = { lat, lng };
      if (this.gmarker) this.gmarker.setPosition(position);
      else {
        this.gmarker = new google.maps.Marker({
          position,
          map: this.gmap,
          draggable: true,
        });
        this.gmarker.addListener('dragend', () => {
          const pos = this.gmarker.getPosition();
          this.setGoogleMarker(pos.lat(), pos.lng(), true);
        });
      }
      this.gmap.panTo(position);
      await this.updatePending(lat, lng, reverse);
    }

    async updatePending(lat, lng, reverse) {
      let label = this.pending && this.pending.label;
      if (reverse || !label) label = await this.reverseGeocode(lat, lng);
      this.pending = {
        lat,
        lng,
        label,
        source: reverse ? 'map' : (this.pending && this.pending.source) || 'map',
      };
      if (this.searchInput) this.searchInput.value = label;
    }

    confirmPending() {
      if (!this.pending) return;
      this.applyLocation(this.pending);
      this.close();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
